//
// Timeline.tsx — the visual timeline. Scrubber, time-axis ruler,
// four parallel tracks (Pan / Zoom / Pitch / Rotate), and coloured
// action blocks the user can drag (move) or resize (by edges).
//
// The component is presentational: all state lives in the parent
// Panel. It invokes callbacks (onActionChange, onSelect, onScrub)
// and receives fresh props on every render. Drag state is local to
// each ActionBlock — started on pointerdown, committed on every
// pointermove via a callback, and torn down on pointerup.
//
// The track area has a fixed width, so px-per-second scales with
// the user's chosen totalSeconds: a 5 s render is twice as wide per
// second as a 10 s render. Snap granularity is 0.05 s.
//
import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { trackOf, type Action, type Timeline as TimelineData, type Track } from '../timeline';
import {
  actionBlockSelectedStyle,
  actionBlockStyle,
  axisLabelStyle,
  axisRowStyle,
  axisStyle,
  playheadStyle,
  resizeHandleStyle,
  scrubberInputStyle,
  scrubberLabelStyle,
  scrubberRowStyle,
  timelineWrapStyle,
  trackAreaStyle,
  trackColors,
  trackLabelStyle,
  trackRowStyle,
} from './styles';

//
// Fixed width of each track area in pixels. The panel is 720 px
// wide; after padding + the 56 px label column, this is what's left.
//
const TRACK_AREA_WIDTH = 620;

//
// Minimum action length in seconds. Prevents the user from
// accidentally collapsing an action to zero width.
//
const MIN_SPAN = 0.2;

//
// Drag snap granularity. Coarse enough that small tremors don't
// shift times, fine enough for useful precision.
//
const SNAP = 0.05;

//
// Which edge of a block (or its body) the user is dragging.
//
type DragMode = 'move' | 'resize-start' | 'resize-end';

interface DragState {
  mode: DragMode;
  startX: number;
  initialStart: number;
  initialEnd: number;
}

export interface TimelineProps {
  timeline: TimelineData;
  totalSeconds: number;
  scrubT: number;
  selectedActionId: string | null;
  onScrub: (t: number) => void;
  onSelect: (id: string | null) => void;
  onActionChange: (action: Action) => void;
}

export function Timeline(props: TimelineProps): JSX.Element {
  const { timeline, totalSeconds, scrubT, selectedActionId, onScrub, onSelect, onActionChange } = props;
  const pxPerSecond = totalSeconds > 0 ? TRACK_AREA_WIDTH / totalSeconds : TRACK_AREA_WIDTH;

  const rows: { track: Track; label: string }[] = [
    { track: 'center', label: 'Pan' },
    { track: 'zoom', label: 'Zoom' },
    { track: 'pitch', label: 'Pitch' },
    { track: 'bearing', label: 'Rotate' },
  ];

  return (
    <div style={timelineWrapStyle}>
      <div style={scrubberRowStyle}>
        <div style={scrubberLabelStyle}>Scrub</div>
        <input
          type="range"
          min="0"
          max={totalSeconds}
          step={SNAP}
          value={scrubT}
          onInput={(e) => onScrub(Number((e.currentTarget as HTMLInputElement).value))}
          style={{ ...scrubberInputStyle, width: `${TRACK_AREA_WIDTH}px` }}
        />
      </div>

      <div style={axisRowStyle}>
        <div style={axisLabelStyle}>{scrubT.toFixed(2)}s</div>
        <TimeAxis totalSeconds={totalSeconds} pxPerSecond={pxPerSecond} />
      </div>

      {rows.map(({ track, label }) => {
        const actionsOnTrack = timeline.actions.filter((a) => trackOf(a) === track);
        return (
          <div key={track} style={trackRowStyle}>
            <div style={trackLabelStyle}>{label}</div>
            <div
              style={{ ...trackAreaStyle, width: `${TRACK_AREA_WIDTH}px` }}
              onClick={(e) => {
                // Deselect when the background itself is clicked.
                // Clicks on any child (block or resize handle) have
                // e.target !== e.currentTarget so they pass through.
                if (e.target === e.currentTarget) onSelect(null);
              }}
            >
              {actionsOnTrack.map((action) => (
                <ActionBlock
                  key={action.id}
                  action={action}
                  siblings={actionsOnTrack.filter((s) => s.id !== action.id)}
                  totalSeconds={totalSeconds}
                  pxPerSecond={pxPerSecond}
                  selected={action.id === selectedActionId}
                  onSelect={() => onSelect(action.id)}
                  onActionChange={onActionChange}
                />
              ))}
              <div style={{ ...playheadStyle, left: `${scrubT * pxPerSecond}px` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

//
// The time axis ruler. Tick density depends on total duration so we
// don't jam "0s 1s 2s 3s 4s 5s 6s 7s 8s 9s 10s" into a narrow strip.
//
function TimeAxis(props: { totalSeconds: number; pxPerSecond: number }): JSX.Element {
  const { totalSeconds, pxPerSecond } = props;
  const step = totalSeconds <= 10 ? 1 : totalSeconds <= 30 ? 2 : 5;

  const ticks: JSX.Element[] = [];
  // Strict inequality: the tick at s === totalSeconds would sit on
  // the right edge and its label text would paint past the
  // timeline's box. Skip it.
  for (let s = 0; s < totalSeconds; s += step) {
    const left = s * pxPerSecond;
    ticks.push(
      <div
        key={s}
        style={{
          position: 'absolute',
          left: `${left}px`,
          top: '0',
          bottom: '0',
          borderLeft: '1px solid #374151',
          paddingLeft: '3px',
          fontSize: '10px',
        }}
      >
        {s}s
      </div>,
    );
  }
  return <div style={{ ...axisStyle, width: `${TRACK_AREA_WIDTH}px` }}>{ticks}</div>;
}

//
// One draggable action block. Manages its own drag state locally
// so siblings re-render cheaply, and commits every change through
// the onActionChange callback so the parent's state stays the
// source of truth.
//
function ActionBlock(props: {
  action: Action;
  siblings: Action[];
  totalSeconds: number;
  pxPerSecond: number;
  selected: boolean;
  onSelect: () => void;
  onActionChange: (action: Action) => void;
}): JSX.Element {
  const { action, siblings, totalSeconds, pxPerSecond, selected, onSelect, onActionChange } = props;
  const [drag, setDrag] = useState<DragState | null>(null);

  const left = action.start * pxPerSecond;
  const width = Math.max(10, (action.end - action.start) * pxPerSecond);

  //
  // Factory that builds a pointerdown handler for a given drag
  // mode. Captures the pointer so subsequent pointermoves are
  // delivered to this element even if the cursor strays.
  //
  const beginDrag = (mode: DragMode) => (e: JSX.TargetedPointerEvent<HTMLDivElement>): void => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({
      mode,
      startX: e.clientX,
      initialStart: action.start,
      initialEnd: action.end,
    });
    onSelect();
  };

  const onPointerMove = (e: JSX.TargetedPointerEvent<HTMLDivElement>): void => {
    if (!drag) return;
    const dt = (e.clientX - drag.startX) / pxPerSecond;
    const { start, end } = clampDrag({
      mode: drag.mode,
      initialStart: drag.initialStart,
      initialEnd: drag.initialEnd,
      dt,
      siblings,
      totalSeconds,
    });
    // Only commit if something actually changed. Cheap guard that
    // dramatically cuts re-renders when the pointer jitters within
    // a single snap cell.
    if (start !== action.start || end !== action.end) {
      onActionChange({ ...action, start, end });
    }
  };

  const onPointerUp = (e: JSX.TargetedPointerEvent<HTMLDivElement>): void => {
    if (!drag) return;
    try {
      (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    } catch { /* already released */ }
    setDrag(null);
  };

  const style: JSX.CSSProperties = {
    ...actionBlockStyle,
    left: `${left}px`,
    width: `${width}px`,
    background: trackColors[trackOf(action)],
    ...(selected ? actionBlockSelectedStyle : {}),
  };

  return (
    <div
      style={style}
      onPointerDown={beginDrag('move')}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {renderBlockLabel(action)}
      <div
        style={{ ...resizeHandleStyle, left: '0' }}
        onPointerDown={beginDrag('resize-start')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div
        style={{ ...resizeHandleStyle, right: '0' }}
        onPointerDown={beginDrag('resize-end')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
    </div>
  );
}

//
// Short human label shown inside an action block. Kept terse on
// purpose so it stays readable inside narrow blocks.
//
function renderBlockLabel(action: Action): string {
  switch (action.type) {
    case 'pan':
      return `→ ${action.target.lat.toFixed(3)}, ${action.target.lng.toFixed(3)}`;
    case 'zoom':
      return `Z → ${action.target.toFixed(2)}`;
    case 'pitch':
      return `P → ${action.target.toFixed(0)}°`;
    case 'rotate':
      return action.mode === 'delta'
        ? `↻ ${action.deltaDegrees >= 0 ? '+' : ''}${action.deltaDegrees.toFixed(0)}°`
        : `↻ → ${action.target.toFixed(0)}°`;
  }
}

//
// Given a drag in progress, return the new { start, end } clamped
// to stay inside [0, totalSeconds] and to not overlap siblings on
// the same track. Snapped to the global SNAP grid.
//
function clampDrag(args: {
  mode: DragMode;
  initialStart: number;
  initialEnd: number;
  dt: number;
  siblings: Action[];
  totalSeconds: number;
}): { start: number; end: number } {
  const { mode, initialStart, initialEnd, dt, siblings, totalSeconds } = args;

  // Nearest sibling end to the left of us — we can't move/shrink
  // past that.
  const leftBound = Math.max(
    0,
    ...siblings.filter((s) => s.end <= initialStart).map((s) => s.end),
  );
  // Nearest sibling start to the right of us — we can't move/grow
  // past that.
  const rightBound = Math.min(
    totalSeconds,
    ...siblings.filter((s) => s.start >= initialEnd).map((s) => s.start),
  );

  let start = initialStart;
  let end = initialEnd;

  if (mode === 'move') {
    const span = initialEnd - initialStart;
    let newStart = initialStart + dt;
    newStart = Math.max(leftBound, Math.min(rightBound - span, newStart));
    start = snap(newStart);
    end = start + span;
  } else if (mode === 'resize-start') {
    let newStart = initialStart + dt;
    newStart = Math.max(leftBound, Math.min(initialEnd - MIN_SPAN, newStart));
    start = snap(newStart);
    end = initialEnd;
  } else {
    let newEnd = initialEnd + dt;
    newEnd = Math.min(rightBound, Math.max(initialStart + MIN_SPAN, newEnd));
    start = initialStart;
    end = snap(newEnd);
  }

  return { start, end };
}

function snap(t: number): number {
  return Math.round(t / SNAP) * SNAP;
}
